import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { User, Ticket, Shield, FileText, Edit, RefreshCw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Header from './Header'
import Footer from './Footer'
import { userService, transactionService } from '../services/api'
import { useRequireAuth } from '../hooks/useAuth'

const MyAccount = () => {
  const { user, loading: authLoading } = useRequireAuth()
  const [activeTab, setActiveTab] = useState('datos')
  const [selectedRaffle, setSelectedRaffle] = useState('all')
  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [editData, setEditData] = useState({})
  const [userTickets, setUserTickets] = useState({})
  const [isRefreshing, setIsRefreshing] = useState(false)
  const navigate = useNavigate()

  // Cargar datos del usuario al montar el componente
  useEffect(() => {
    if (user && !authLoading) {
      setUserData(user)
      setEditData(user)
      loadUserTickets()
      setLoading(false)
    }
  }, [user, authLoading])

  const loadUserData = async () => {
    try {
      const response = await userService.getProfile()
      setUserData(response.user)
      setEditData(response.user)
    } catch (error) {
      console.error('Error al cargar datos del usuario:', error)
    }
  }

  // Cargar boletos del usuario
  const loadUserTickets = async () => {
    try {
      const response = await transactionService.getMyTickets()
      
      // La respuesta ya viene agrupada por sorteo
      const groupedTickets = {}
      
      if (response.tickets_by_sorteo) {
        Object.keys(response.tickets_by_sorteo).forEach(sorteoId => {
          const sorteoData = response.tickets_by_sorteo[sorteoId]
          groupedTickets[sorteoId] = {
            name: sorteoData.sorteo.name,
            tickets: sorteoData.tickets.map(ticket => ({
              numero: ticket.numero,
              status: ticket.status,
              reference: ticket.reference,
              urlPay: ticket.urlPay,
              transaction_total: ticket.transaction_total,
              transaction_ticket_count: ticket.transaction_ticket_count
            }))
          }
        })
      }
      
      setUserTickets(groupedTickets)
    } catch (error) {
      console.error('Error al cargar boletos:', error)
    }
  }

  const refreshTickets = async () => {
    setIsRefreshing(true)
    try {
      await loadUserTickets()
    } catch (error) {
      console.error('Error al actualizar boletos:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  // Mostrar loading mientras se verifica la autenticación
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Verificando autenticación...</p>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'datos', label: 'Mis Datos', icon: User },
    { id: 'boletos', label: 'Mis Boletos', icon: Ticket },
    { id: 'privacidad', label: 'Políticas de Privacidad', icon: Shield },
    { id: 'terminos', label: 'Términos y Condiciones', icon: FileText }
  ]

  const handleEdit = () => {
    setEditMode(true)
  }

  const handleSave = async () => {
    try {
      await userService.updateProfile({
        name: editData.name,
        phone: editData.phone,
        city: editData.city
      })
      
      setUserData({ ...userData, ...editData })
      setEditMode(false)
      alert('Datos actualizados correctamente')
    } catch (error) {
      alert(error.message || 'Error al actualizar datos')
    }
  }

  const handleCancel = () => {
    setEditData(userData)
    setEditMode(false)
  }

  const handleInputChange = (field, value) => {
    setEditData({ ...editData, [field]: value })
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'datos':
        if (loading) {
          return (
            <div className="flex justify-center items-center py-12">
              <div className="text-white">Cargando datos del usuario...</div>
            </div>
          )
        }

        if (!userData) {
          return (
            <div className="flex justify-center items-center py-12">
              <div className="text-white">Error al cargar los datos del usuario</div>
            </div>
          )
        }

        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-white">Información Personal</h3>
              {!editMode ? (
                <button 
                  onClick={handleEdit}
                  className="flex items-center space-x-2 text-primary-400 hover:text-primary-300 transition-colors"
                >
                  <Edit className="h-4 w-4" />
                  <span>Editar</span>
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button 
                    onClick={handleSave}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Guardar
                  </button>
                  <button 
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nombre Completo</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editData.name || ''}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-primary-400"
                    />
                  ) : (
                    <div className="bg-white/10 border border-white/20 rounded-lg p-3 text-white">
                      {userData.name}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <div className="bg-white/10 border border-white/20 rounded-lg p-3 text-white">
                    {userData.email}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Celular</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editData.phone || ''}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-primary-400"
                    />
                  ) : (
                    <div className="bg-white/10 border border-white/20 rounded-lg p-3 text-white">
                      {userData.phone}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Documento</label>
                  <div className="bg-white/10 border border-white/20 rounded-lg p-3 text-white">
                    {userData.typedoc}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Número de Documento</label>
                  <div className="bg-white/10 border border-white/20 rounded-lg p-3 text-white">
                    {userData.doc}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Ciudad</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editData.city || ''}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none focus:border-primary-400"
                    />
                  ) : (
                    <div className="bg-white/10 border border-white/20 rounded-lg p-3 text-white">
                      {userData.city}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )

      case 'boletos':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-semibold text-white">Mis Boletos</h3>
                <button
                  onClick={refreshTickets}
                  disabled={isRefreshing}
                  className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white p-2 rounded-lg transition-colors flex items-center gap-1"
                  title="Actualizar estado de boletos"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <select
                value={selectedRaffle}
                onChange={(e) => setSelectedRaffle(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">Todas las membresías</option>
                {Object.entries(userTickets).map(([id, raffle]) => (
                  <option key={id} value={id}>{raffle.name}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-4">
              {Object.entries(userTickets)
                .filter(([id]) => selectedRaffle === 'all' || selectedRaffle === id)
                .map(([id, raffle]) => (
                  <div key={id} className="bg-white/10 border border-white/20 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-white mb-4">{raffle.name}</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {raffle.tickets.map((ticket, index) => (
                        <div key={index} className="relative">
                          <div
                            className={`text-center py-2 px-3 rounded-lg font-mono font-bold cursor-pointer ${
                              ticket.status === 'pending'
                                ? 'bg-gray-500 text-gray-300 border-2 border-gray-400 hover:bg-gray-600'
                                : 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                            }`}
                            onClick={() => {
                              if (ticket.status === 'pending' && ticket.urlPay) {
                                window.open(ticket.urlPay, '_blank')
                              }
                            }}
                            title={ticket.status === 'pending' ? `Referencia: ${ticket.reference} - Click para pagar` : `Boleto #${ticket.numero}`}
                          >
                            {ticket.status === 'pending' ? (
                              <span className="text-red-500 text-xl font-bold">?</span>
                            ) : (
                              ticket.numero
                            )}
                          </div>
                          {ticket.status === 'pending' && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">!</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 text-sm space-y-1">
                      <p className="text-gray-300">
                        Total de boletos: {raffle.tickets.length}
                      </p>
                      {raffle.tickets.some(t => t.status === 'pending') && (
                        <div className="space-y-2">
                          <p className="text-yellow-400">
                            Boletos pendientes de pago: {raffle.tickets.filter(t => t.status === 'pending').length}
                          </p>
                          {(() => {
                            const pendingTicket = raffle.tickets.find(t => t.status === 'pending')
                            return pendingTicket && pendingTicket.transaction_total && (
                              <p className="text-green-400 font-semibold">
                                Monto a pagar: ${pendingTicket.transaction_total.toLocaleString()} COP
                                <span className="text-gray-400 text-xs ml-2">
                                  ({pendingTicket.transaction_ticket_count} boleto{pendingTicket.transaction_ticket_count > 1 ? 's' : ''})
                                </span>
                              </p>
                            )
                          })()}
                          <button
                            onClick={() => {
                              const pendingTicket = raffle.tickets.find(t => t.status === 'pending')
                              if (pendingTicket && pendingTicket.urlPay) {
                                window.open(pendingTicket.urlPay, '_blank')
                              }
                            }}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
                          >
                            <span>💳</span>
                            Ir a Pagar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        )

      case 'privacidad':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white">Políticas de Privacidad</h3>
            <div className="bg-white/10 border border-white/20 rounded-lg p-6 text-gray-300 space-y-4">
              <h4 className="text-lg font-semibold text-white">Recopilación de Información</h4>
              <p>
                Recopilamos información personal que usted nos proporciona directamente cuando se registra en nuestra plataforma, 
                participa en membresías, o se comunica con nosotros.
              </p>
              
              <h4 className="text-lg font-semibold text-white">Uso de la Información</h4>
              <p>
                Utilizamos su información personal para procesar sus participaciones en membresías, comunicarnos con usted, 
                y mejorar nuestros servicios.
              </p>
              
              <h4 className="text-lg font-semibold text-white">Protección de Datos</h4>
              <p>
                Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger su información personal 
                contra el acceso no autorizado, alteración, divulgación o destrucción.
              </p>
              
              <h4 className="text-lg font-semibold text-white">Derechos del Usuario</h4>
              <p>
                Usted tiene derecho a acceder, rectificar, cancelar y oponerse al tratamiento de sus datos personales. 
                Para ejercer estos derechos, puede contactarnos a través de nuestros canales de atención.
              </p>
            </div>
          </div>
        )

      case 'terminos':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white">Términos y Condiciones</h3>
            <div className="bg-white/10 border border-white/20 rounded-lg p-6 text-gray-300 space-y-4">
              <h4 className="text-lg font-semibold text-white">Aceptación de Términos</h4>
              <p>
                Al utilizar nuestros servicios, usted acepta estar sujeto a estos términos y condiciones. 
                Si no está de acuerdo con alguna parte de estos términos, no debe utilizar nuestros servicios.
              </p>
              
              <h4 className="text-lg font-semibold text-white">Participación en Membresías</h4>
              <p>
                La participación en membresías está sujeta a las reglas específicas de cada membresía. 
                Los participantes deben ser mayores de 18 años y residir en Colombia.
              </p>
              
              <h4 className="text-lg font-semibold text-white">Responsabilidades del Usuario</h4>
              <p>
                Los usuarios son responsables de mantener la confidencialidad de su cuenta y contraseña, 
                y de todas las actividades que ocurran bajo su cuenta.
              </p>
              
              <h4 className="text-lg font-semibold text-white">Limitación de Responsabilidad</h4>
              <p>
                Nuestra responsabilidad se limita al valor de los boletos adquiridos. No seremos responsables 
                por daños indirectos, incidentales o consecuentes.
              </p>
              
              <h4 className="text-lg font-semibold text-white">Modificaciones</h4>
              <p>
                Nos reservamos el derecho de modificar estos términos en cualquier momento. 
                Las modificaciones entrarán en vigor inmediatamente después de su publicación.
              </p>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      <Header />
      
      <div className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Mi Cuenta</h1>
            <p className="text-gray-300">Gestiona tu información y boletos</p>
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar with Tabs */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <div className="space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                            : 'text-gray-300 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                        <span className="font-medium">{tab.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20"
              >
                {renderTabContent()}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  )
}

export default MyAccount